// pages/user-management.ts — admin-only user CRUD.
//
// Mirrors frontend2/Admin.jsx :: UserManagementPage.
//   • Two tabs: Requestors (Role=User) · Approvers & Admins (Role=Approver|Admin)
//   • Status chip filter: All / Active / Disabled
//   • Search by name or email
//   • Inline row actions: Edit · Disable/Enable · Delete
//   • Create-user modal (returns temp password + welcome-email flag)
//   • Edit-user modal (FullName / Role / Phone / Organization; Email read-only)
//
// Server enforces: users with grants can't be deleted (error: user_has_grants);
// disable them instead. Email uniqueness on create.

import type { Page, PageContext } from '../router';
import { html, esc, fmtDate } from '../util';

interface User {
  UserId: number;
  FullName: string;
  Email: string;
  Role: 'User' | 'Approver' | 'Admin' | string;
  PhoneNumber?: string | null;
  OrganizationName?: string | null;
  IsActive?: boolean;
  CreatedAt?: string;
}
interface CreateResult {
  user: User;
  tempPassword: string;
  welcomeEmailQueued: boolean;
}

type TabId = 'requestors' | 'approvers';
type StatusFilter = 'All' | 'Active' | 'Disabled';

function roleBadge(role: string): string {
  let bg = 'var(--paper-2)', fg = 'var(--ink-soft)';
  if (role === 'Admin')    { bg = 'var(--accent-soft)';  fg = 'var(--accent)'; }
  if (role === 'Approver') { bg = 'var(--primary-soft)'; fg = 'var(--st-approved)'; }
  return `<span style="padding: 2px 8px; border-radius: 999px; font-size: 11.5px; font-weight: 600;
    letter-spacing: 0.04em; text-transform: uppercase; background: ${bg}; color: ${fg};">${esc(role)}</span>`;
}

function statusPill(active: boolean): string {
  return `<span style="padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600;
    background: ${active ? 'var(--primary-soft)' : '#EFD7D7'};
    color: ${active ? 'var(--st-approved)' : 'var(--st-denied)'};">${active ? 'Active' : 'Disabled'}</span>`;
}

function createModal(): string {
  return html`
    <div class="modal fade" id="umCreateModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 520px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" style="margin: 0; font-size: 20px;">Create user</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div id="um-create-form">
              <div class="gf-field">
                <label class="form-label" for="um-c-name">Full name<span style="color: var(--st-denied); margin-left: 2px;">*</span></label>
                <input class="form-control" id="um-c-name"/>
              </div>
              <div class="gf-field">
                <label class="form-label" for="um-c-email">Email<span style="color: var(--st-denied); margin-left: 2px;">*</span></label>
                <input type="email" class="form-control" id="um-c-email"/>
              </div>
              <div class="gf-field">
                <label class="form-label" for="um-c-role">Role<span style="color: var(--st-denied); margin-left: 2px;">*</span></label>
                <select class="form-select" id="um-c-role">
                  <option value="User">User (requestor)</option>
                  <option value="Approver" selected>Approver</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              <div class="gf-field">
                <label class="form-label" for="um-c-phone">Phone (optional)</label>
                <input class="form-control" id="um-c-phone"/>
              </div>
              <div class="gf-field">
                <label class="form-label" for="um-c-org">Organization (optional)</label>
                <input class="form-control" id="um-c-org"/>
              </div>
              <label style="display: flex; gap: 10px; align-items: center; cursor: pointer; margin-top: 8px;">
                <input type="checkbox" id="um-c-welcome" checked style="accent-color: var(--primary);"/>
                <span style="font-size: 14px;">Queue welcome email with temp password</span>
              </label>
              <div id="um-c-error" class="gf-field-error" style="display: none; margin-top: 12px;"></div>
            </div>
            <div id="um-create-result" style="display: none;"></div>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal" id="um-c-cancel">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="confirm-create" id="um-c-confirm">Create user</button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" id="um-c-done"
                    style="display: none;">Done</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function editModal(): string {
  return html`
    <div class="modal fade" id="umEditModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" style="max-width: 500px;">
        <div class="modal-content">
          <div class="modal-header" style="border-bottom: 1px solid var(--rule);">
            <h3 class="t-h4 serif" id="um-e-title" style="margin: 0; font-size: 20px;">Edit user</h3>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <input type="hidden" id="um-e-id"/>
            <div class="gf-field">
              <label class="form-label" for="um-e-name">Full name<span style="color: var(--st-denied); margin-left: 2px;">*</span></label>
              <input class="form-control" id="um-e-name"/>
            </div>
            <div class="gf-field">
              <label class="form-label" for="um-e-email">Email</label>
              <input class="form-control" id="um-e-email" disabled/>
            </div>
            <div class="gf-field">
              <label class="form-label" for="um-e-role">Role</label>
              <select class="form-select" id="um-e-role">
                <option value="User">User (requestor)</option>
                <option value="Approver">Approver</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            <div class="gf-field">
              <label class="form-label" for="um-e-phone">Phone</label>
              <input class="form-control" id="um-e-phone"/>
            </div>
            <div class="gf-field">
              <label class="form-label" for="um-e-org">Organization</label>
              <input class="form-control" id="um-e-org"/>
            </div>
            <div id="um-e-error" class="gf-field-error" style="display: none; margin-top: 8px;"></div>
          </div>
          <div class="modal-footer" style="border-top: 1px solid var(--rule);">
            <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" data-action="confirm-edit" id="um-e-confirm">Save changes</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

const UserManagementPage: Page = {
  guard(ctx: PageContext): string | null {
    if (!ctx.user) return 'login';
    if (ctx.userRole !== 'admin') return 'landing';
    return null;
  },

  render(_ctx: PageContext): string {
    return html`
      <main class="wrap" style="max-width: 1240px; padding: 56px 24px 96px;">
        <div class="d-flex" style="justify-content: space-between; align-items: flex-end;
                                   flex-wrap: wrap; gap: 16px; margin-bottom: 24px;">
          <div>
            <div class="eyebrow">Users</div>
            <h1 class="t-h2" style="margin: 16px 0 0;">User management.</h1>
            <p class="t-body" style="margin: 6px 0 0;">Manage requestors, approvers, and administrators.</p>
          </div>
          <button type="button" class="btn btn-primary" data-action="open-create">+ Create user</button>
        </div>

        <div class="card" style="padding: 0; overflow: hidden;">
          <div class="d-flex" style="justify-content: space-between; padding: 14px 16px;
                                     border-bottom: 1px solid var(--rule); flex-wrap: wrap; gap: 12px;">
            <div class="d-flex" id="um-tabs" style="gap: 8px; flex-wrap: wrap;"></div>
            <div class="d-flex" style="gap: 8px; align-items: center; flex-wrap: wrap;">
              <div class="d-flex" id="um-status-filter" style="gap: 4px;"></div>
              <input id="um-search" class="form-control" placeholder="Search by name or email…"
                     style="width: 240px; padding: 8px 12px;"/>
            </div>
          </div>
          <div id="um-table-wrap">
            <p class="t-body" style="padding: 60px 24px; text-align: center;">Loading users…</p>
          </div>
        </div>

        ${{ __raw: createModal() + editModal() }}
      </main>
    `;
  },

  mount(root: HTMLElement, ctx: PageContext): void {
    let alive = true;
    let activeTab: TabId = 'requestors';
    let activeFilter: StatusFilter = 'All';
    let search = '';
    let users: User[] = [];

    const tabsEl   = root.querySelector('#um-tabs')          as HTMLElement | null;
    const filterEl = root.querySelector('#um-status-filter') as HTMLElement | null;
    const tableEl  = root.querySelector('#um-table-wrap')    as HTMLElement | null;
    const searchEl = root.querySelector('#um-search')        as HTMLInputElement | null;

    const Bs = (): { Modal: { getOrCreateInstance(el: HTMLElement): { show(): void; hide(): void } } } | undefined =>
      (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(el: HTMLElement): { show(): void; hide(): void } } } }).bootstrap;
    function showModal(sel: string): void {
      const el = root.querySelector(sel) as HTMLElement | null;
      if (el) Bs()?.Modal.getOrCreateInstance(el).show();
    }
    function hideModal(sel: string): void {
      const el = root.querySelector(sel) as HTMLElement | null;
      if (el) Bs()?.Modal.getOrCreateInstance(el).hide();
    }

    function paintTabs(): void {
      if (!tabsEl) return;
      const items: Array<[TabId, string]> = [['requestors', 'Requestors'], ['approvers', 'Approvers & admins']];
      tabsEl.innerHTML = items.map(([id, label]) => `
        <button type="button" data-action="set-tab" data-tab="${id}"
                style="padding: 8px 14px; font-size: 14px; font-weight: 500; border-radius: 999px;
                       border: none; cursor: pointer;
                       background: ${activeTab === id ? 'var(--primary)' : 'transparent'};
                       color: ${activeTab === id ? 'var(--paper)' : 'var(--ink-2)'};">${label}</button>
      `).join('');
    }

    function paintFilter(): void {
      if (!filterEl) return;
      const opts: StatusFilter[] = ['All', 'Active', 'Disabled'];
      filterEl.innerHTML = opts.map(f => `
        <button type="button" data-action="set-filter" data-filter="${f}"
                style="padding: 6px 12px; border-radius: 999px; font-size: 12.5px; font-weight: 500;
                       border: none; cursor: pointer;
                       background: ${activeFilter === f ? 'var(--primary)' : 'var(--paper-2)'};
                       color: ${activeFilter === f ? 'var(--paper)' : 'var(--ink-2)'};">${f}</button>
      `).join('');
    }

    function filteredRows(): User[] {
      const list = activeTab === 'requestors'
        ? users.filter(u => u.Role === 'User')
        : users.filter(u => u.Role === 'Approver' || u.Role === 'Admin');
      const lc = search.toLowerCase();
      return list.filter(u => {
        const matchesSearch = !search
          || (u.FullName || '').toLowerCase().includes(lc)
          || (u.Email    || '').toLowerCase().includes(lc);
        const matchesStatus = activeFilter === 'All'
          || (activeFilter === 'Active' ? !!u.IsActive : !u.IsActive);
        return matchesSearch && matchesStatus;
      });
    }

    function paintTable(): void {
      if (!tableEl) return;
      const rows = filteredRows();
      if (rows.length === 0) {
        tableEl.innerHTML = `
          <div style="padding: 48px 40px; text-align: center;">
            <p class="t-body" style="margin: 0;">No users match the current filter.</p>
          </div>
        `;
        return;
      }
      const thirdCol = activeTab === 'requestors' ? 'Organization' : 'Role';
      const trs = rows.map(u => {
        const third = activeTab === 'requestors'
          ? esc(u.OrganizationName || '\u2014')
          : roleBadge(u.Role);
        return `
          <tr>
            <td style="font-weight: 500;">${esc(u.FullName)}</td>
            <td class="t-small">${esc(u.Email)}</td>
            <td>${third}</td>
            <td class="num">${esc(fmtDate(u.CreatedAt))}</td>
            <td>${statusPill(!!u.IsActive)}</td>
            <td>
              <div class="d-flex" style="gap: 4px;">
                <button type="button" class="btn btn-text btn-sm" data-action="edit"  data-user-id="${u.UserId}">Edit</button>
                <button type="button" class="btn btn-text btn-sm" data-action="toggle" data-user-id="${u.UserId}"
                        style="color: ${u.IsActive ? 'var(--st-denied)' : 'var(--primary)'};">${u.IsActive ? 'Disable' : 'Enable'}</button>
                <button type="button" class="btn btn-text btn-sm" data-action="delete" data-user-id="${u.UserId}"
                        style="color: var(--st-denied);">Delete</button>
              </div>
            </td>
          </tr>
        `;
      }).join('');
      tableEl.innerHTML = `
        <table class="ledger">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>${thirdCol}</th>
              <th style="width: 130px;">Created</th>
              <th style="width: 100px;">Status</th>
              <th style="width: 240px;"></th>
            </tr>
          </thead>
          <tbody>${trs}</tbody>
        </table>
      `;
    }

    function paintAll(): void { paintTabs(); paintFilter(); paintTable(); }

    // ── Data ──────────────────────────────────────────────────────
    async function refresh(): Promise<void> {
      try {
        const list = await window.api.listUsers() as User[];
        if (!alive) return; users = list || []; paintTable();
      } catch (e) { console.error(e); }
    }

    // ── Actions ───────────────────────────────────────────────────
    async function toggleActive(userId: number): Promise<void> {
      const u = users.find(x => x.UserId === userId);
      if (!u) return;
      try { await window.api.updateUser(userId, { IsActive: !u.IsActive }); await refresh(); }
      catch (e) { alert((e as Error).message); }
    }

    async function deleteUser(userId: number): Promise<void> {
      const u = users.find(x => x.UserId === userId);
      if (!u) return;
      const ok = window.confirm(
        `Permanently delete ${u.FullName} (${u.Email})?\n\n` +
        `This will also delete their messages and status-history entries. ` +
        `Grants assigned to them will be unassigned.\n\n` +
        `Users who own grant applications cannot be deleted — disable them instead.`
      );
      if (!ok) return;
      try { await window.api.deleteUser(userId); await refresh(); }
      catch (err) {
        const e = err as { payload?: { error?: string }; message?: string };
        if (e.payload?.error === 'user_has_grants') {
          alert('This user has submitted grants and cannot be deleted. Disable the account instead.');
        } else {
          alert(e.message || 'Delete failed.');
        }
      }
    }

    function openCreate(): void {
      // Reset modal to form mode
      const ids = ['name', 'email', 'phone', 'org'] as const;
      ids.forEach(k => {
        const el = root.querySelector<HTMLInputElement>(`#um-c-${k}`); if (el) el.value = '';
      });
      const roleSel = root.querySelector<HTMLSelectElement>('#um-c-role'); if (roleSel) roleSel.value = 'Approver';
      const welcome = root.querySelector<HTMLInputElement>('#um-c-welcome'); if (welcome) welcome.checked = true;
      const err     = root.querySelector<HTMLElement>('#um-c-error'); if (err) { err.style.display = 'none'; err.textContent = ''; }
      const form    = root.querySelector<HTMLElement>('#um-create-form'); if (form) form.style.display = '';
      const result  = root.querySelector<HTMLElement>('#um-create-result');
      if (result) { result.style.display = 'none'; result.innerHTML = ''; }
      (root.querySelector('#um-c-confirm') as HTMLElement | null)?.style.removeProperty('display');
      (root.querySelector('#um-c-cancel')  as HTMLElement | null)?.style.removeProperty('display');
      const done = root.querySelector<HTMLElement>('#um-c-done'); if (done) done.style.display = 'none';
      showModal('#umCreateModal');
    }

    async function confirmCreate(): Promise<void> {
      const name    = (root.querySelector<HTMLInputElement>('#um-c-name')?.value  || '').trim();
      const email   = (root.querySelector<HTMLInputElement>('#um-c-email')?.value || '').trim();
      const role    = (root.querySelector<HTMLSelectElement>('#um-c-role')?.value || 'Approver');
      const phone   = (root.querySelector<HTMLInputElement>('#um-c-phone')?.value || '').trim();
      const org     = (root.querySelector<HTMLInputElement>('#um-c-org')?.value   || '').trim();
      const welcome = !!root.querySelector<HTMLInputElement>('#um-c-welcome')?.checked;
      const errEl   = root.querySelector<HTMLElement>('#um-c-error');
      const confirm = root.querySelector<HTMLButtonElement>('#um-c-confirm');

      if (!name || !email) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = 'Name and email are required.'; }
        return;
      }
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
      if (confirm) { confirm.disabled = true; confirm.textContent = 'Creating…'; }
      try {
        const res = await window.api.createUser({
          FullName: name, Email: email, Role: role,
          PhoneNumber: phone || null,
          OrganizationName: org || null,
          sendWelcomeEmail: welcome,
        }) as CreateResult;
        await refresh();
        // Switch modal into "result" mode.
        const form    = root.querySelector<HTMLElement>('#um-create-form');
        const result  = root.querySelector<HTMLElement>('#um-create-result');
        const cancel  = root.querySelector<HTMLElement>('#um-c-cancel');
        const done    = root.querySelector<HTMLElement>('#um-c-done');
        if (form)    form.style.display = 'none';
        if (cancel)  cancel.style.display = 'none';
        if (confirm) confirm.style.display = 'none';
        if (done)    done.style.display = '';
        if (result) {
          result.style.display = '';
          result.innerHTML = `
            <div style="background: var(--primary-soft); border: 1px solid var(--primary);
                        border-radius: 10px; padding: 14px 16px; margin-bottom: 16px;">
              <strong>${esc(res.user.FullName)}</strong> created as <strong>${esc(res.user.Role)}</strong>.
            </div>
            <div style="background: var(--paper-2); padding: 14px 16px; border-radius: 10px;">
              <div class="label">Temporary password</div>
              <p style="font-family: monospace; font-size: 16px; font-weight: 500; margin: 6px 0 10px;
                        word-break: break-all;">${esc(res.tempPassword)}</p>
              <p class="t-small" style="margin: 0;">${
                res.welcomeEmailQueued
                  ? 'Welcome email queued (Mailgun pending — deliver manually until then).'
                  : 'No welcome email sent — share this password directly with the user.'
              }</p>
            </div>
          `;
        }
      } catch (err) {
        const e = err as { payload?: { error?: string }; message?: string };
        if (errEl) {
          errEl.style.display = 'block';
          errEl.textContent = e.payload?.error === 'email_already_exists'
            ? 'An account with that email already exists.'
            : (e.message || 'Create failed.');
        }
      } finally {
        if (confirm) { confirm.disabled = false; confirm.textContent = 'Create user'; }
      }
    }

    function openEdit(userId: number): void {
      const u = users.find(x => x.UserId === userId);
      if (!u) return;
      const titleEl = root.querySelector<HTMLElement>('#um-e-title');
      if (titleEl) titleEl.textContent = `Edit ${u.FullName}`;
      (root.querySelector<HTMLInputElement>('#um-e-id')!).value    = String(u.UserId);
      (root.querySelector<HTMLInputElement>('#um-e-name')!).value  = u.FullName || '';
      (root.querySelector<HTMLInputElement>('#um-e-email')!).value = u.Email || '';
      (root.querySelector<HTMLSelectElement>('#um-e-role')!).value = u.Role || 'User';
      (root.querySelector<HTMLInputElement>('#um-e-phone')!).value = u.PhoneNumber || '';
      (root.querySelector<HTMLInputElement>('#um-e-org')!).value   = u.OrganizationName || '';
      const errEl = root.querySelector<HTMLElement>('#um-e-error');
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
      showModal('#umEditModal');
    }

    async function confirmEdit(): Promise<void> {
      const userId  = Number((root.querySelector<HTMLInputElement>('#um-e-id')?.value) || 0);
      const name    = (root.querySelector<HTMLInputElement>('#um-e-name')?.value  || '').trim();
      const role    = (root.querySelector<HTMLSelectElement>('#um-e-role')?.value || 'User');
      const phone   = (root.querySelector<HTMLInputElement>('#um-e-phone')?.value || '').trim();
      const org     = (root.querySelector<HTMLInputElement>('#um-e-org')?.value   || '').trim();
      const errEl   = root.querySelector<HTMLElement>('#um-e-error');
      const confirm = root.querySelector<HTMLButtonElement>('#um-e-confirm');
      if (!userId || !name) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = 'Name is required.'; }
        return;
      }
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
      if (confirm) { confirm.disabled = true; confirm.textContent = 'Saving…'; }
      try {
        await window.api.updateUser(userId, {
          FullName: name,
          PhoneNumber: phone || null,
          OrganizationName: org || null,
          Role: role,
        });
        await refresh();
        hideModal('#umEditModal');
      } catch (err) {
        if (errEl) { errEl.style.display = 'block'; errEl.textContent = (err as Error).message || 'Save failed.'; }
      } finally {
        if (confirm) { confirm.disabled = false; confirm.textContent = 'Save changes'; }
      }
    }

    // ── Listeners ────────────────────────────────────────────────
    root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const actionEl = t.closest('[data-action]') as HTMLElement | null;
      if (!actionEl) return;
      const action = actionEl.getAttribute('data-action');
      switch (action) {
        case 'set-tab': {
          activeTab = (actionEl.getAttribute('data-tab') as TabId) || 'requestors';
          search = ''; if (searchEl) searchEl.value = '';
          activeFilter = 'All';
          paintAll();
          break;
        }
        case 'set-filter':
          activeFilter = (actionEl.getAttribute('data-filter') as StatusFilter) || 'All';
          paintAll();
          break;
        case 'open-create':    openCreate(); break;
        case 'confirm-create': void confirmCreate(); break;
        case 'confirm-edit':   void confirmEdit(); break;
        case 'edit': {
          const uid = Number(actionEl.getAttribute('data-user-id')); if (uid) openEdit(uid);
          break;
        }
        case 'toggle': {
          const uid = Number(actionEl.getAttribute('data-user-id')); if (uid) void toggleActive(uid);
          break;
        }
        case 'delete': {
          const uid = Number(actionEl.getAttribute('data-user-id')); if (uid) void deleteUser(uid);
          break;
        }
      }
    });

    if (searchEl) {
      searchEl.addEventListener('input', () => { search = searchEl.value || ''; paintTable(); });
    }

    // ── Initial load ─────────────────────────────────────────────
    paintAll();
    window.api.listUsers()
      .then(list => {
        if (!alive) return;
        users = (list as User[]) || [];
        paintTable();
      })
      .catch(err => {
        if (!alive || !tableEl) return;
        tableEl.innerHTML = `
          <div style="padding: 48px 40px; text-align: center;">
            <p class="t-body" style="color: var(--st-denied); margin: 0;">
              Could not load users (${esc((err as Error).message || 'network')}).
            </p>
          </div>
        `;
      });

    (UserManagementPage as Page & { _alive?: () => void })._alive = () => { alive = false; };

    // ctx unused but kept for the Page interface
    void ctx;
  },

  unmount(): void {
    const stop = (UserManagementPage as Page & { _alive?: () => void })._alive;
    if (stop) stop();
  },
};

export default UserManagementPage;
